import { FC, memo, useCallback } from 'react'

import { Form } from 'brief-form'
import { countries } from 'commons/types/dictionaries'
import { determineIssuingStateData } from 'commons/utils'
import { InfoSet } from 'ui/components/Info'
import { TextInput } from 'ui/components/InputV2'
import { Select } from 'ui/components/Select'
import { Col, FormRow } from 'ui/themes/globalStyles'

import { OnboardingFormFieldNames } from '../../enums'
import {
  CompanyPhysicalAddressChangeHandler,
  CompanyPhysicalAddressFormData,
  CompanyPhysicalAddressFormValue
} from '../../types/forms'
import { useCompanyPhysicalAddressForm } from './useCompanyPhysicalAddressForm'

type CompanyPhysicalAddressFormProps = {
  companyIndex: number
  value: CompanyPhysicalAddressFormData
  onChange: CompanyPhysicalAddressChangeHandler
}

export const CompanyPhysicalAddressForm: FC<CompanyPhysicalAddressFormProps> = memo((props) => {
  const { companyIndex, value, onChange } = props
  const { Field, config } = useCompanyPhysicalAddressForm({ companyIndex, formData: value, onChange })
  const originalChangeHandler = config.onChange

  config.onChange = useCallback(
    (v: CompanyPhysicalAddressFormValue, e) => {
      if (config.value.country && config.value.country !== v.country) {
        v.state = undefined
        delete e['state']
      }
      originalChangeHandler(v, e)
    },
    [originalChangeHandler, config.value.country]
  )

  return (
    <Form config={config}>
      <InfoSet legend="Physical Address">
        <FormRow>
          <Col>
            <Field
              name={OnboardingFormFieldNames.COMPANY_STREET}
              label="Street"
              input={TextInput}
              inputProps={{
                'data-qa': 'Street',
                maxLength: 255
              }}
            />
          </Col>
          <Col>
            <Field
              name={OnboardingFormFieldNames.COMPANY_CITY}
              label="City"
              input={TextInput}
              inputProps={{
                'data-qa': 'City',
                maxLength: 40
              }}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col>
            <Field
              name={OnboardingFormFieldNames.COMPANY_COUNTRY}
              label="Country"
              input={Select}
              inputProps={{
                'data-qa': 'Country',
                'options-data-qa': 'Country',
                data: countries
              }}
            />
          </Col>
          <Col>
            <Field
              name={OnboardingFormFieldNames.COMPANY_STATE}
              label="State/Province"
              input={Select}
              inputProps={{
                'data-qa': 'State/Province',
                'options-data-qa': 'State/Province',
                data: determineIssuingStateData(config.value.country)
              }}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col>
            <Field
              name={OnboardingFormFieldNames.COMPANY_POSTAL_CODE}
              label="Zip/Postal Code"
              input={TextInput}
              inputProps={{
                'data-qa': 'Zip/Postal Code',
                maxLength: 20
              }}
            />
          </Col>
        </FormRow>
      </InfoSet>
    </Form>
  )
})
